import { useState } from "react";
import { EntityCustomViewParams } from "@firecms/core";
import { Campaign } from "../collections/campaigns";
import { useAdminApi } from "../hooks/useAdminApi";
import type { components } from "../types/api.generated";

type CampaignResponse = components["schemas"]["CampaignResponse"];

export function CampaignActionsView({ entity }: EntityCustomViewParams<Campaign>) {
    const { fetchApi } = useAdminApi();
    const [executing, setExecuting] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    const campaign = entity?.values;

    async function executeCampaign() {
        if (!entity?.id) return;

        if (!window.confirm("Are you sure you want to execute this campaign now? This will grant credits to ALL targeted users.")) {
            return;
        }

        setExecuting(true);
        setMessage(null);

        try {
            const result = await fetchApi(`/admin/api/campaigns/${entity.id}/execute`, {
                method: "POST",
            }) as CampaignResponse;

            setMessage({
                type: "success",
                text: result.message || "Campaign executed successfully!",
            });

            // Reload after success
            setTimeout(() => window.location.reload(), 2000);
        } catch (e: any) {
            console.error("Failed to execute campaign:", e);
            setMessage({
                type: "error",
                text: e.message || "Failed to execute campaign via server.",
            });
        } finally {
            setExecuting(false);
        }
    }

    if (!campaign) {
        return <div className="p-4 text-gray-400">No campaign data available</div>;
    }

    const canExecute = campaign.status === "draft" || campaign.status === "scheduled";

    return (
        <div className="p-6 space-y-6 bg-gray-950 min-h-full text-slate-200">
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-2">🚀 Execute Campaign</h3>
                <p className="text-slate-400 mb-6">
                    Manually trigger the execution of this promotional campaign.
                    This will immediately search for users matching the criteria and grant them
                    <strong> {campaign.text_credits} text</strong> and <strong> {campaign.voice_credits} voice</strong> credits.
                </p>

                {message && (
                    <div className={`mb-6 p-4 rounded-lg border ${message.type === "success"
                        ? "bg-green-500/10 border-green-500/30 text-green-400"
                        : "bg-red-500/10 border-red-500/30 text-red-400"
                        }`}>
                        {message.text}
                    </div>
                )}

                <div className="flex items-center gap-4">
                    <button
                        onClick={executeCampaign}
                        disabled={executing || !canExecute}
                        className={`px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2 ${canExecute && !executing
                            ? "bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/20"
                            : "bg-slate-800 text-slate-500 cursor-not-allowed"
                            }`}
                    >
                        {executing ? (
                            <>
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                Executing...
                            </>
                        ) : (
                            <>
                                <span>⚡</span> Execute Now
                            </>
                        )}
                    </button>

                    {!canExecute && (
                        <span className="text-sm text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20">
                            Campaign is {campaign.status}
                        </span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                    <h4 className="text-slate-400 text-sm font-medium mb-3 uppercase tracking-wider">Targeting Summary</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-slate-500">Type:</span>
                            <span className="text-white font-mono">{campaign.target_type}</span>
                        </div>
                        {campaign.target_tiers && (
                            <div className="flex justify-between">
                                <span className="text-slate-500">Tiers:</span>
                                <span className="text-white font-mono">{campaign.target_tiers.join(", ")}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                    <h4 className="text-slate-400 text-sm font-medium mb-3 uppercase tracking-wider">Rewards</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-slate-500">Text Credits:</span>
                            <span className="text-green-400 font-bold">+{campaign.text_credits}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Voice Credits:</span>
                            <span className="text-blue-400 font-bold">+{campaign.voice_credits}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
